import * as React from "react";
import { supabase, isDemoMode, isSupabaseConfigured } from "@/lib/supabase/client";
import { DUMMY_USER } from "@/lib/dummy-data";
import type { AppUser } from "@/types/user";
import type { SignInInput, SignUpInput } from "@/types/auth";

interface AuthContextValue {
  user: AppUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (input: SignInInput) => Promise<void>;
  signUp: (input: SignUpInput) => Promise<{ requiresEmailConfirmation: boolean }>;
  confirmEmail: (email: string, token: string) => Promise<void>;
  resendConfirmation: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

const DUMMY_SESSION_KEY = "atelier.dummy-auth-session";

function toAppUser(sessionUser: {
  id: string;
  email?: string;
  created_at: string;
  user_metadata?: Record<string, unknown>;
}): AppUser {
  const metadata = sessionUser.user_metadata ?? {};
  return {
    id: sessionUser.id,
    email: sessionUser.email ?? "",
    fullName: typeof metadata.full_name === "string" ? metadata.full_name : sessionUser.email ?? "",
    avatarUrl: typeof metadata.avatar_url === "string" ? metadata.avatar_url : undefined,
    organizationName: typeof metadata.organization_name === "string" ? metadata.organization_name : undefined,
    createdAt: sessionUser.created_at,
  };
}

/**
 * Provides the authenticated user across the app.
 *
 * Two modes, chosen automatically:
 *  - Supabase mode (isSupabaseConfigured === true): real Supabase Auth,
 *    session persisted and refreshed by supabase-js.
 *  - Dummy mode (default, no project configured yet): a fixed demo user
 *    "logs in" and is persisted to localStorage only, so the app shell,
 *    routing, and RBAC can be reviewed end to end before a backend exists.
 *
 * Every consumer (ProtectedRoute, UserMenu, etc.) reads from this context
 * only — none of them know or care which mode is active.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<AppUser | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    if (isSupabaseConfigured) {
      supabase.auth.getSession().then(({ data }) => {
        const sessionUser = data.session?.user;
        setUser(sessionUser ? toAppUser(sessionUser) : null);
        setIsLoading(false);
      });

      const { data: subscription } = supabase.auth.onAuthStateChange((_event, session) => {
        const sessionUser = session?.user;
        setUser(sessionUser ? toAppUser(sessionUser) : null);
      });

      return () => subscription.subscription.unsubscribe();
    }

    if (!isDemoMode) {
      // A production deployment must never fall back to the shared demo
      // identity when its backend configuration is incomplete.
      setUser(null);
      setIsLoading(false);
      return undefined;
    }

    // Local development only: restore a previously "signed in" demo session.
    const stored = window.localStorage.getItem(DUMMY_SESSION_KEY);
    setUser(stored ? DUMMY_USER : null);
    setIsLoading(false);
    return undefined;
  }, []);

  const signIn = React.useCallback(async (input: SignInInput) => {
    if (isSupabaseConfigured) {
      const { error } = await supabase.auth.signInWithPassword(input);
      if (error) throw error;
      return;
    }
    if (!isDemoMode) throw new Error("Atelier Suite is temporarily unavailable because authentication is not configured.");
    // Local demo mode accepts any credentials and logs in as the demo user.
    await new Promise((resolve) => setTimeout(resolve, 400));
    window.localStorage.setItem(DUMMY_SESSION_KEY, "1");
    setUser(DUMMY_USER);
  }, []);

  const signUp = React.useCallback(async (input: SignUpInput) => {
    if (isSupabaseConfigured) {
      const { data, error } = await supabase.auth.signUp({
        email: input.email,
        password: input.password,
        options: {
          emailRedirectTo: `${window.location.origin}/login`,
          data: { full_name: input.fullName, organization_name: input.organizationName },
        },
      });
      if (error) throw error;
      // Confirmed accounts may receive an obfuscated successful response
      // with no identities instead of an explicit duplicate-email error.
      if (!data.session && data.user?.identities?.length === 0) {
        throw Object.assign(new Error("User already registered"), { code: "user_already_exists" });
      }
      return { requiresEmailConfirmation: !data.session };
    }
    if (!isDemoMode) throw new Error("Atelier Suite is temporarily unavailable because authentication is not configured.");
    await new Promise((resolve) => setTimeout(resolve, 400));
    window.localStorage.setItem(DUMMY_SESSION_KEY, "1");
    setUser({ ...DUMMY_USER, fullName: input.fullName, email: input.email });
    return { requiresEmailConfirmation: false };
  }, []);

  const confirmEmail = React.useCallback(async (email: string, token: string) => {
    if (!isSupabaseConfigured) return;
    const { error } = await supabase.auth.verifyOtp({ email, token, type: "signup" });
    if (error) throw error;
  }, []);

  const resendConfirmation = React.useCallback(async (email: string) => {
    if (!isSupabaseConfigured) return;
    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
      options: { emailRedirectTo: `${window.location.origin}/login` },
    });
    if (error) throw error;
  }, []);

  const signOut = React.useCallback(async () => {
    if (isSupabaseConfigured) {
      await supabase.auth.signOut();
      return;
    }
    if (!isDemoMode) return;
    window.localStorage.removeItem(DUMMY_SESSION_KEY);
    setUser(null);
  }, []);

  const value = React.useMemo<AuthContextValue>(
    () => ({ user, isLoading, isAuthenticated: Boolean(user), signIn, signUp, confirmEmail, resendConfirmation, signOut }),
    [user, isLoading, signIn, signUp, confirmEmail, resendConfirmation, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error("useAuthContext must be used within an AuthProvider");
  return ctx;
}
